"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";

const OfficeContent: React.FC = () => {
  const insuranceProviders = [
    {
      name: "BCBS",
      image: "/images/custom_images_insurances_bcbs.webp",
      url: "https://www.bcbs.com/",
    },
    {
      name: "Cigna",
      image: "/images/custom_images_insurances_cigna.webp",
      url: "https://www.cigna.com/",
    },
    {
      name: "MetLife",
      image: "/images/custom_images_insurances_metlife.webp",
      url: "https://www.metlife.com/",
    },
    {
      name: "UH",
      image: "/images/custom_images_insurances_uh.webp",
      url: "https://www.uhc.com/",
    },
    {
      name: "Aetna",
      image: "/images/custom_images_insurances_aetna.webp",
      url: "https://www.aetna.com/",
    },
    {
      name: "Guardian",
      image: "/images/custom_images_insurances_guardian.webp",
      url: "https://www.guardianlife.com/",
    },
    {
      name: "Delta",
      image: "/images/custom_images_insurances_delta.webp",
      url: "https://www.deltadental.com/",
    },
  ];

  return (
    <div className="w-full">
      {/* Hero Text Section with Floating Elements */}
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 overflow-hidden">
       
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50">
              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
                If you&#39;re looking for a highly trained and experienced dentist in <span className="font-semibold bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] bg-clip-text text-transparent">Queens</span>, you&#39;ve come to the right place. Our state-of-the-art practice provides top-quality dental care and upholds the strictest sterilization standards and safety protocols to protect our patients&#39; health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Scheduling Card */}
            <div className="group relative bg-gradient-to-br from-[#AE7D2F]/5 to-[#ECD05B]/5 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 border border-[#AE7D2F]/10 hover:border-[#AE7D2F]/30">
              <div className="absolute top-6 right-6 w-16 h-16 bg-gradient-to-br from-[#AE7D2F] to-[#ECD05B] rounded-2xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300">
                <span className="text-white text-2xl">📅</span>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-[#AE7D2F] transition-colors">
                Easy Scheduling
              </h3>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                Book your appointment with just a few clicks. We make it simple and convenient.
              </p>
              
              <Link
                href="https://app.nexhealth.com/appt/smile4me_dental_care?lid=77847&tracking_key=b2d71902-5ce0-43c1-bc95-24cdee27de9a"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Book Online
                <span className="ml-2">→</span>
              </Link>
            </div>

            {/* Payment Card */}
            <div className="group relative bg-gradient-to-br from-[#AE7D2F]/5 to-[#ECD05B]/5 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 border border-[#AE7D2F]/10 hover:border-[#AE7D2F]/30">
              <div className="absolute top-6 right-6 w-16 h-16 bg-gradient-to-br from-[#AE7D2F] to-[#ECD05B] rounded-2xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300">
                <span className="text-white text-2xl">💳</span>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-[#AE7D2F] transition-colors">
                Flexible Payment
              </h3>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                We accept all major credit cards, cash, checks, and most PPO insurance plans.
              </p>
              
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-[#AE7D2F] rounded-full mr-2"></span>
                  Mastercard, Visa, Amex, Discover
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-[#ECD05B] rounded-full mr-2"></span>
                  Most PPO Insurance Plans
                </div>
              </div>
            </div>

            {/* Location Card */}
            <div className="group relative bg-gradient-to-br from-[#AE7D2F]/5 to-[#ECD05B]/5 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 border border-[#AE7D2F]/10 hover:border-[#AE7D2F]/30">
              <div className="absolute top-6 right-6 w-16 h-16 bg-gradient-to-br from-[#AE7D2F] to-[#ECD05B] rounded-2xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300">
                <span className="text-white text-2xl">📍</span>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-[#AE7D2F] transition-colors">
                Prime Location
              </h3>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong>31-57 Steinway St</strong><br />
                Queens, NY 11103
              </p>
              
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-[#AE7D2F] rounded-full mr-2"></span>
                  R/M to Steinway St (5 min walk)
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-[#ECD05B] rounded-full mr-2"></span>
                  Q101/Q66 Bus Routes
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Partners */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Insurance Partners
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] mx-auto rounded-full"></div>
          </div>

          {/* Medicaid Notice */}
          <div className="bg-gray-100 border-l-4 border-gray-400 p-4 rounded-r-lg mb-10">
            <p className="text-gray-700 font-medium">
              Please Note: We are not a Medicaid provider.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-6">
            {insuranceProviders.map((provider, index) => (
              <Link
                key={index}
                href={provider.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-[#AE7D2F]/30"
                aria-label={`Visit ${provider.name} website`}
              >
                <div className="aspect-square flex items-center justify-center">
                  <Image
                    src={provider.image}
                    alt={provider.name}
                    width={80}
                    height={40}
                    className="object-contain group-hover:scale-110 transition-transform duration-300"
                    sizes="120px"
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Transportation & Directions */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Getting Here is Easy
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            
            {/* Subway */}
            <div className="bg-gradient-to-br from-[#AE7D2F]/10 to-[#ECD05B]/10 rounded-2xl p-8 border border-[#AE7D2F]/20">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#AE7D2F] to-[#ECD05B] rounded-xl flex items-center justify-center mr-4">
                  <span className="text-white text-xl">🚇</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">By Subway</h3>
              </div>
              
              <div className="space-y-4 text-sm text-gray-700">
                <div className="bg-white/80 rounded-lg p-3">
                  <div className="font-semibold text-gray-900 mb-1">R/M Lines</div>
                  <div>Steinway St Station → 5 min walk north</div>
                </div>
                <div className="bg-white/80 rounded-lg p-3">
                  <div className="font-semibold text-gray-900 mb-1">N/W Lines</div>
                  <div>36th Ave Station → 6 min walk east + 5 min north</div>
                </div>
              </div>
            </div>

            {/* Bus */}
            <div className="bg-gradient-to-br from-[#AE7D2F]/10 to-[#ECD05B]/10 rounded-2xl p-8 border border-[#AE7D2F]/20">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#AE7D2F] to-[#ECD05B] rounded-xl flex items-center justify-center mr-4">
                  <span className="text-white text-xl">🚌</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">By Bus</h3>
              </div>
              
              <div className="space-y-4 text-sm text-gray-700">
                <div className="bg-white/80 rounded-lg p-3">
                  <div className="font-semibold text-gray-900 mb-1">Q101 Bus</div>
                  <div>Steinway St/31st Ave stop (right near office)</div>
                </div>
                <div className="bg-white/80 rounded-lg p-3">
                  <div className="font-semibold text-gray-900 mb-1">Q66 Bus</div>
                  <div>Steinway St/31st Ave → 1 min walk north</div>
                </div>
              </div>
            </div>

            {/* Car */}
            <div className="bg-gradient-to-br from-[#AE7D2F]/10 to-[#ECD05B]/10 rounded-2xl p-8 border border-[#AE7D2F]/20 md:col-span-2 lg:col-span-1">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#AE7D2F] to-[#ECD05B] rounded-xl flex items-center justify-center mr-4">
                  <span className="text-white text-xl">🚗</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">By Car</h3>
              </div>
              
              <div className="space-y-4 text-sm text-gray-700">
                <div className="bg-white/80 rounded-lg p-3">
                  <div className="font-semibold text-gray-900 mb-1">From Manhattan</div>
                  <div>Queensboro Bridge → Northern Blvd → Steinway St</div>
                </div>
                <div className="bg-white/80 rounded-lg p-3">
                  <div className="font-semibold text-gray-900 mb-1">From Brooklyn</div>
                  <div>BQE (I-278 E) → Exit 38 → Northern Blvd</div>
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Directions */}
          <div className="bg-gradient-to-r from-[#AE7D2F]/10 to-[#ECD05B]/10 rounded-3xl p-8 border border-[#AE7D2F]/20">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Get Personalized Directions
              </h3>
              <p className="text-gray-700 mb-6">
                Enter your location for turn-by-turn directions to our office
              </p>
              
              <form 
                action="https://maps.google.com/maps" 
                method="get" 
                target="_blank" 
                className="flex flex-col sm:flex-row gap-4 items-center justify-center max-w-md mx-auto"
              >
                <input
                  type="text"
                  name="saddr"
                  id="saddr"
                  aria-label="Enter Your Zip Code"
                  className="flex-1 px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#AE7D2F] focus:border-transparent text-center sm:text-left"
                  placeholder="Enter your ZIP code"
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] text-white font-bold rounded-2xl hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
                >
                  Get Directions
                </button>
                <input type="hidden" name="daddr" value="31-57 Steinway St., Queens, NY 11103" />
                <input type="hidden" name="hl" value="en" />
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OfficeContent;